import type { RecoverParticipantInput, RecoverParticipantResult } from '@citizen-walk/shared-types';
import { httpsCallable } from 'firebase/functions';
import { useCallback, useRef, useState } from 'react';
import { auth, functions } from '../../lib/firebase';

/**
 * `recoverParticipant` callable wrapper hook (Phase 5、issueRecoveryCode と対の操作)。
 *
 * 旧 `RecoveryRecoverPage.tsx` の callable 呼出部分を client hook 化:
 *   - rate limit 超過 (100 req/min/IP、issue #28) は HttpsError(`resource-exhausted`) として error
 *   - 失敗時は not-found (recovery code mismatch) / invalid-argument (フォーマット) を判別
 *   - 成功時に Custom Claims が更新されるが、client は `getIdToken(true)` で
 *     ID トークン強制 refresh しないと Rules 反映されない (docs/97 §3 sprint 2 警戒メモ #2)
 *
 * R-70 P6 (2026-09-03): **受け取り側の上書き確認**。
 *   サーバは受け取り側 (この端末) に別人の参加情報があると `failed-precondition` + details
 *   `{ reason: 'existing-membership', existingNickname, incomingNickname }` を返して止まる。
 *   本 hook はそれを `needs-confirm` 状態にして UI に確認画面を出させ、`confirm()` で
 *   `confirmOverwrite: true` を付けて同じ入力を再送する。
 */

export type RecoverParticipantState =
  | { status: 'idle'; data: null; error: null }
  | { status: 'pending'; data: null; error: null }
  | { status: 'success'; data: RecoverParticipantResult; error: null }
  | { status: 'error'; data: null; error: Error }
  | {
      status: 'needs-confirm';
      data: null;
      error: null;
      /** この端末に既にある参加情報のニックネーム (取得できなければ null)。 */
      existingNickname: string | null;
      /** 復帰コードの持ち主のニックネーム (取得できなければ null)。 */
      incomingNickname: string | null;
    };

export interface ExistingMembershipDetails {
  reason: 'existing-membership';
  existingNickname: string | null;
  incomingNickname: string | null;
}

/** callable エラーの details が「受け取り側に別人の参加情報あり」かを判定する pure 関数。 */
export function parseExistingMembershipDetails(details: unknown): ExistingMembershipDetails | null {
  if (!details || typeof details !== 'object') return null;
  const d = details as Record<string, unknown>;
  if (d.reason !== 'existing-membership') return null;
  return {
    reason: 'existing-membership',
    existingNickname: typeof d.existingNickname === 'string' ? d.existingNickname : null,
    incomingNickname: typeof d.incomingNickname === 'string' ? d.incomingNickname : null,
  };
}

const IDLE: RecoverParticipantState = { status: 'idle', data: null, error: null };

export function useRecoverParticipant(): {
  state: RecoverParticipantState;
  call: (input: RecoverParticipantInput) => Promise<void>;
  /** `needs-confirm` のとき、了承済みとして同じ入力を `confirmOverwrite: true` で再送する。 */
  confirm: () => Promise<void>;
  reset: () => void;
} {
  const [state, setState] = useState<RecoverParticipantState>(IDLE);
  // needs-confirm で保留した入力 (confirm で再送する)。
  const pendingRef = useRef<RecoverParticipantInput | null>(null);

  const run = useCallback(async (input: RecoverParticipantInput) => {
    setState({ status: 'pending', data: null, error: null });
    try {
      const fn = httpsCallable<RecoverParticipantInput, RecoverParticipantResult>(
        functions,
        'recoverParticipant',
      );
      const res = await fn(input);

      // 成功時: Custom Claims を新 caller uid に反映済 → ID トークン強制 refresh
      // (docs/97 §3 sprint 2 警戒メモ #2、Rules が即時 accessibleDistricts を見られるように)
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
      }

      pendingRef.current = null;
      setState({ status: 'success', data: res.data, error: null });
    } catch (e) {
      const raw = e as { code?: string; message?: string; details?: unknown };
      const code = typeof raw.code === 'string' ? raw.code : '';
      if (code.includes('failed-precondition')) {
        const details = parseExistingMembershipDetails(raw.details);
        if (details) {
          pendingRef.current = input;
          setState({
            status: 'needs-confirm',
            data: null,
            error: null,
            existingNickname: details.existingNickname,
            incomingNickname: details.incomingNickname,
          });
          return;
        }
      }
      let message = raw.message ?? String(e);
      if (code.includes('resource-exhausted')) {
        message = '復帰の試行回数上限に達しました。1 分後に再試行してください。';
      } else if (code.includes('not-found')) {
        message = 'リカバリコードが見つかりません。コードを確認してください。';
      } else if (code.includes('invalid-argument')) {
        message = 'リカバリコードの形式が正しくありません (12 文字)。';
      }
      const err = new Error(message);
      (err as Error & { code?: string }).code = code;
      setState({ status: 'error', data: null, error: err });
    }
  }, []);

  const call = useCallback((input: RecoverParticipantInput) => run(input), [run]);

  const confirm = useCallback(async () => {
    const pending = pendingRef.current;
    if (!pending) return;
    await run({ ...pending, confirmOverwrite: true });
  }, [run]);

  const reset = useCallback(() => {
    pendingRef.current = null;
    setState(IDLE);
  }, []);

  return { state, call, confirm, reset };
}
